"use client";

import React from "react";
import { ConfigProvider, Dropdown } from "antd";
import type { DropdownProps } from "antd";
import { orionTheme } from "../theme/orion-theme";
import clsx from "clsx";
import { DownOutlined } from "@ant-design/icons";
import "../styles/thumbnail.css";

export interface ThumbnailProps {
  /**
   * Image source URL
   */
  src?: string;

  /**
   * Alt text for the image
   */
  alt?: string;

  /**
   * Main title text
   */
  title: string;

  /**
   * Subtitle text (e.g., "3 variants")
   */
  subtitle?: string;

  /**
   * Whether to show dropdown functionality
   */
  showDropdown?: boolean;

  /**
   * Dropdown menu items
   */
  dropdownItems?: Array<{
    key: string;
    label: string;
    onClick?: () => void;
  }>;

  /**
   * Callback when dropdown is clicked
   */
  onDropdownClick?: () => void;

  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;

  /**
   * Thumbnail size
   */
  size?: "small" | "medium" | "large";

  /**
   * Click handler for the entire thumbnail
   */
  onClick?: () => void;
}

/**
 * Thumbnail component - Image thumbnail with optional dropdown
 *
 * This component provides a thumbnail display based on Figma design specifications
 * with optional dropdown functionality.
 *
 * Features:
 * - Figma design specifications (40x40px image, specific colors, typography)
 * - Optional dropdown functionality
 * - Multiple sizes
 * - Automatic theming
 * - Tailwind class override support
 *
 * @example
 * ```tsx
 * // Basic thumbnail
 * <Thumbnail
 *   src="/path/to/image.jpg"
 *   alt="Product image"
 *   title="Medium"
 *   subtitle="3 variants"
 * />
 *
 * // With dropdown
 * <Thumbnail
 *   src="/path/to/image.jpg"
 *   alt="Product image"
 *   title="Medium"
 *   subtitle="3 variants"
 *   showDropdown
 *   dropdownItems={[
 *     { key: 'edit', label: 'Edit', onClick: handleEdit },
 *     { key: 'delete', label: 'Delete', onClick: handleDelete },
 *   ]}
 * />
 *
 * // Without dropdown
 * <Thumbnail
 *   src="/path/to/image.jpg"
 *   alt="Product image"
 *   title="Medium"
 *   showDropdown={false}
 * />
 * ```
 */
export const Thumbnail: React.FC<ThumbnailProps> = ({
  src,
  alt = "Thumbnail",
  title,
  subtitle,
  showDropdown = false,
  dropdownItems = [],
  onDropdownClick,
  className,
  size = "medium",
  onClick,
}) => {
  const getSizeClass = (size: string) => {
    switch (size) {
      case "small":
        return "orion-thumbnail--small";
      case "large":
        return "orion-thumbnail--large";
      case "medium":
      default:
        return "orion-thumbnail--medium";
    }
  };

  const thumbnailContent = (
    <div
      className={clsx(
        "orion-thumbnail",
        getSizeClass(size),
        { "orion-thumbnail--clickable": onClick },
        className
      )}
      onClick={onClick}
    >
      <div className="orion-thumbnail-image">
        {src ? (
          <img src={src} alt={alt} />
        ) : (
          <div className="orion-thumbnail-placeholder">
            <span>📦</span>
          </div>
        )}
      </div>
      <div className="orion-thumbnail-content">
        <div className="orion-thumbnail-title">{title}</div>
        {subtitle && <div className="orion-thumbnail-subtitle">{subtitle}</div>}
      </div>
      {showDropdown && (
        <div className="orion-thumbnail-dropdown">
          <DownOutlined />
        </div>
      )}
    </div>
  );

  if (showDropdown && dropdownItems.length > 0) {
    const menu = {
      items: dropdownItems.map((item) => ({
        key: item.key,
        label: item.label,
        onClick: item.onClick,
      })),
      style: {
        borderRadius: "4px",
        padding: "8px",
        boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
        border: "1px solid #D0D5DD",
        minWidth: 120,
      },
    };

    return (
      <ConfigProvider theme={orionTheme}>
        <Dropdown
          menu={menu}
          trigger={["click"]}
          className="orion-thumbnail-dropdown-wrapper"
        >
          {thumbnailContent}
        </Dropdown>
      </ConfigProvider>
    );
  }

  return <ConfigProvider theme={orionTheme}>{thumbnailContent}</ConfigProvider>;
};

Thumbnail.displayName = "Thumbnail";
